<template>
  <div>
    <p class="tw-text-muted tw-mb-4 tw-text-sm">
      Central hub for every MLS data vendor Brokurz may use — admin dashboards,
      apply links, and docs. Live: <strong>MLS Grid</strong>, <strong>Trestle</strong>,
      <strong>Bridge</strong>. Next: Bright direct / board-by-board expansions.
    </p>

    <div class="ops-row tw-mb-4">
      <router-link
        v-for="item in quickOps"
        :key="item.to"
        :to="item.to"
        class="ops-chip"
      >
        <span class="ops-chip-label">{{ item.label }}</span>
        <span class="ops-chip-hint">{{ item.hint }}</span>
      </router-link>
    </div>

    <div class="filter-row tw-mb-4">
      <InputText
        v-model="q"
        class="tw-w-full filter-input"
        placeholder="Filter vendors…"
      />
      <div class="status-pills">
        <button
          v-for="s in statusFilters"
          :key="s.value"
          type="button"
          class="status-pill"
          :class="{ 'is-on': statusFilter === s.value }"
          @click="statusFilter = s.value"
        >
          {{ s.label }}
        </button>
      </div>
    </div>

    <div class="vendor-grid">
      <article
        v-for="v in filtered"
        :key="v.id"
        class="vendor-card"
        :data-status="v.status"
      >
        <header class="vendor-head">
          <img
            class="vendor-logo"
            :src="v.logoUrl"
            :alt="`${v.name} logo`"
            width="40"
            height="40"
            loading="lazy"
            @error="onLogoError"
          />
          <div class="vendor-titles">
            <h2 class="vendor-name">{{ v.name }}</h2>
            <span class="vendor-badge" :data-status="v.status">{{ statusLabel(v.status) }}</span>
          </div>
        </header>
        <p class="vendor-blurb">{{ v.blurb }}</p>
        <div class="vendor-links">
          <a
            v-for="link in v.links"
            :key="link.href + link.label"
            :href="link.href"
            class="vendor-link"
            :class="{ 'is-primary': link.primary }"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ link.label }}
            <i class="pi pi-external-link" aria-hidden="true" />
          </a>
        </div>
        <footer class="vendor-foot">
          <router-link v-if="v.internalDoc" :to="v.internalDoc" class="vendor-doc">
            In-app docs
          </router-link>
          <span v-if="v.vendorKey" class="vendor-key">feeds.vendor = {{ v.vendorKey }}</span>
        </footer>
      </article>
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import { VENDOR_HUB, VENDOR_QUICK_OPS } from '../lib/vendorHub'

const FALLBACK_LOGO =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect fill="#e8e6e1" width="64" height="64" rx="12"/><text x="32" y="38" text-anchor="middle" font-family="system-ui" font-size="22" fill="#1a1a1a">MLS</text></svg>'
  )

export default {
  name: 'VendorsPage',
  components: { InputText },
  data() {
    return {
      q: '',
      statusFilter: 'all',
      vendors: VENDOR_HUB,
      quickOps: VENDOR_QUICK_OPS,
      statusFilters: [
        { value: 'all', label: 'All' },
        { value: 'live', label: 'Live' },
        { value: 'apply', label: 'Apply' },
        { value: 'reference', label: 'Reference' },
      ],
    }
  },
  computed: {
    filtered() {
      const needle = (this.q || '').trim().toLowerCase()
      return this.vendors.filter((v) => {
        if (this.statusFilter !== 'all' && v.status !== this.statusFilter) return false
        if (!needle) return true
        const hay = [v.name, v.blurb, v.domain, v.vendorKey]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return hay.includes(needle)
      })
    },
  },
  methods: {
    statusLabel(status) {
      if (status === 'live') return 'Live for Brokurz'
      if (status === 'apply') return 'Apply / expand'
      return 'Reference'
    },
    onLogoError(e) {
      e.target.src = FALLBACK_LOGO
    },
  },
}
</script>

<style scoped>
.ops-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
}

.ops-chip {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid var(--bk-line, #e4e2dc);
  border-radius: 10px;
  background: #fff;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.ops-chip:hover {
  border-color: var(--bk-ink, #1a1a1a);
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
}

.ops-chip-label {
  font-weight: 650;
  font-size: 0.92rem;
}

.ops-chip-hint {
  font-size: 0.75rem;
  color: var(--bk-muted, #6b7280);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.filter-input {
  max-width: 22rem;
}

.status-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.status-pill {
  border: 1px solid var(--bk-line, #e4e2dc);
  background: #fff;
  border-radius: 999px;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  cursor: pointer;
  color: var(--bk-muted, #6b7280);
}

.status-pill.is-on {
  background: var(--bk-ink, #1a1a1a);
  border-color: var(--bk-ink, #1a1a1a);
  color: #fff;
}

.vendor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.85rem;
}

.vendor-card {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 1rem 1.05rem 0.9rem;
  border: 1px solid var(--bk-line, #e4e2dc);
  border-radius: 12px;
  background: #fff;
}

.vendor-card[data-status='live'] {
  border-color: #b7d4c0;
  background: linear-gradient(180deg, #f5faf6 0%, #fff 42%);
}

.vendor-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.vendor-logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: contain;
  background: #f4f3ef;
  border: 1px solid var(--bk-line, #e4e2dc);
  padding: 4px;
}

.vendor-titles {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.vendor-name {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
}

.vendor-badge {
  display: inline-flex;
  align-self: flex-start;
  font-size: 0.68rem;
  font-weight: 650;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  background: #f0eeea;
  color: #4b5563;
}

.vendor-badge[data-status='live'] {
  background: #d8efe0;
  color: #166534;
}

.vendor-badge[data-status='apply'] {
  background: #efe6d4;
  color: #92400e;
}

.vendor-blurb {
  margin: 0;
  font-size: 0.84rem;
  line-height: 1.45;
  color: var(--bk-muted, #4b5563);
  flex: 1;
}

.vendor-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.vendor-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.55rem;
  border-radius: 7px;
  border: 1px solid var(--bk-line, #e4e2dc);
  background: #fafaf8;
  color: var(--bk-ink, #1a1a1a);
  text-decoration: none;
  font-size: 0.78rem;
  font-weight: 550;
}

.vendor-link i {
  font-size: 0.65rem;
  opacity: 0.55;
}

.vendor-link.is-primary {
  background: var(--bk-ink, #1a1a1a);
  border-color: var(--bk-ink, #1a1a1a);
  color: #fff;
}

.vendor-link.is-primary i {
  opacity: 0.85;
}

.vendor-link:hover {
  border-color: var(--bk-ink, #1a1a1a);
}

.vendor-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding-top: 0.35rem;
  border-top: 1px solid var(--bk-line, #eeebe4);
  font-size: 0.75rem;
}

.vendor-doc {
  color: var(--bk-ink, #1a1a1a);
  font-weight: 600;
  text-decoration: none;
}

.vendor-doc:hover {
  text-decoration: underline;
}

.vendor-key {
  color: var(--bk-muted, #6b7280);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem;
}

@media (max-width: 900px) {
  .ops-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .ops-row {
    grid-template-columns: 1fr;
  }

  .filter-input {
    max-width: none;
  }
}
</style>
